import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { serviceAreaEndpoints } from '@/lib/api/endpoints';

/**
 * Local mutation hook for the service-area editor's polygon → ZIP lookup.
 *
 * Targets the lightweight `POST /service-areas/preview-from-polygon`
 * endpoint, which only resolves the centroid containment (sub-second) and
 * does NOT fetch per-ZIP Ziptax rates. The heavier rate-fetch lives behind
 * the `POST /tax-zones/populate` BullMQ job, which the bulk-save flow
 * triggers asynchronously after the user clicks Save — so the picker UX
 * stays responsive even when the drawn polygon covers hundreds of ZIPs.
 *
 * (The legacy `POST /tax-zones/preview-from-polygon` endpoint still exists
 * for the tax-zone admin UX, which needs rates inline.)
 */

/** GeoJSON polygon shape we POST to the BE. `[lng, lat]` per GeoJSON spec. */
export interface PolygonPreviewRingPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface PolygonPreviewInput {
  polygon: PolygonPreviewRingPolygon;
}

/** Trimmed response — the editor only needs the ZIP list + state hint. */
export interface PolygonPreviewResult {
  /** ZIP codes whose centroids fell inside the polygon. */
  zips: string[];
  /**
   * De-duplicated 2-letter state codes covering every ZIP in `zips`.
   * The map uses this to eagerly fetch the per-state ZCTA GeoJSON
   * needed to paint the bulk selection — without it, ZIPs in states
   * the user hasn't panned over silently fail to highlight.
   */
  states: string[];
}

/**
 * BE response shape. Mirrors the operations DTO at
 * `apps/operations/src/modules/taxes/service-areas/dto/preview-polygon-zips.dto.ts`.
 */
interface PreviewPolygonZipsResponse {
  zips: string[];
  states: string[];
}

/**
 * TanStack Query mutation around the polygon preview endpoint. Fires
 * on demand (the user draws a polygon → we POST it → we get the ZIP list).
 *
 * The default `useApiMutation` error toast is left ON — the parent map
 * component doesn't render its own polygon error UI, so a quick toast is
 * the right UX. The map also calls its own `toast.success` on the happy
 * path with a count of selected ZIPs.
 */
export function usePolygonPreview() {
  return useApiMutation<PolygonPreviewResult, PolygonPreviewInput>({
    mutationFn: async (input) => {
      const res = await apiPost<PreviewPolygonZipsResponse, PolygonPreviewInput>(
        operationsClient,
        serviceAreaEndpoints.previewFromPolygon(),
        input,
      );
      return {
        zips: Array.isArray(res.zips) ? res.zips : [],
        // Defensive: an older deploy of operations might not yet emit
        // `states`. Fall back to an empty array — the FE's pan-driven
        // loader will still paint visible states (the bug-fix
        // degenerates to its pre-fix behaviour rather than crashing).
        states: Array.isArray(res.states) ? res.states : [],
      };
    },
  });
}
